import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Settings2, UserRound } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { getSuperDeviceToken } from "./SuperOtpStep";

/** Hook: the Super Admin's own security status (two-step deliverability, trusted browsers, recent events). */
export function useSuperSecurity(open = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/auth/security", { params: { device_token: getSuperDeviceToken() || undefined } });
      setData(r.data);
    } catch { /* banner simply stays hidden */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) reload(); }, [open, reload]);
  return { data, loading, reload };
}

/**
 * Phase 154: shown on the Super Admin dashboard while the sign-in OTP cannot actually be delivered, so the
 * account owner knows the second step is being skipped and how to switch it back on.
 */
export const OPEN_ACCOUNT_EVENT = "mc:open-account";
export const openMyAccount = () => window.dispatchEvent(new CustomEvent(OPEN_ACCOUNT_EVENT));

export function TwoStepBanner({ onOpenAccount = openMyAccount }) {
  const { data } = useSuperSecurity(true);
  const ts = data?.two_step;
  if (!ts || ts.deliverable) return null;
  const needsMobile = ts.reason === "no_mobile";
  return (
    <Card className="border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20 mb-6" data-testid="dashboard-two-step-banner">
      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Two-step sign-in is not active — your Super Admin account signs in with the password only</p>
            <p className="text-xs text-amber-800/90 dark:text-amber-300/90" data-testid="dashboard-two-step-reason">{ts.reason_text}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {needsMobile || ts.reason === "mobile_not_allowlisted" ? (
            <Button variant="outline" size="sm" onClick={onOpenAccount} data-testid="dashboard-two-step-account-button">
              <UserRound className="h-4 w-4 mr-1.5" /> My account
            </Button>
          ) : null}
          {ts.reason !== "no_mobile" && (
            <Button asChild variant="outline" size="sm" data-testid="dashboard-two-step-gateway-link">
              <Link to="/super/notifications"><Settings2 className="h-4 w-4 mr-1.5" /> Notification gateway</Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
